import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { canRemoveAgents, type AppRole } from '@infras/shared-types';
import { CopyButton } from '../components/CopyButton';
import { AgentCommandDialog } from '../components/AgentCommandDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { KebabMenu } from '../components/KebabMenu';
import { ScannedAt } from '../components/ScannedAt';
import { SortHeader } from '../components/SortHeader';
import { useMe } from '../lib/useMe';
import { compareBy, useUrlSort, useUrlState } from '../lib/useUrlState';
import { timeAgo, type AgentRelease, type AgentVersionRollup } from '../lib/agentCommands';
import {
  AGENT_CHECKLIST,
  AGENT_RED_MINUTES,
  DRIFT_FILTER,
  LEGACY_GONE_FILTER,
  REG_FAILED_FILTER,
  RED_FILTER,
  RED_RULE,
  YELLOW_RULE,
  type AgentChecklist,
  type AtlasRestoreTarget,
} from '../lib/agentLifecycle';
import {
  agentLevel,
  agentRowClass,
  isBulkSelectable,
  isRedAgentRow,
  isRemovableAgent,
  isRemovedAgent,
  summarizeAgents,
  type FleetSummary,
} from '../lib/agentFleet';
import { AgentRemoveDialog } from './cluster-access/AgentRemoveDialog';
import { AtlasRestoreDialog } from './cluster-access/AtlasRestoreDialog';
import { RefreshButton } from '../components/RefreshButton';

/**
 * Agent fleet — DaemonSet agents that report node egress IPs for Atlas IP
 * whitelisting, and, since the command
 * plane, the surface an operator issues work from: per-cluster desired version +
 * rollout progress, and per-node logs / restart through the approve-first dialog.
 */

const AGENTS = gql`
  query Agents {
    agents {
      agentId
      clusterName
      nodeName
      publicIp
      status
      failedHealthChecks
      lastSeenAt
      agentVersion
      agentImage
      isLeader
      leaderHolderIdentity
      leaderObservedAt
      capabilities
    }
    agentStatusEvents(limit: 20) {
      id
      agentId
      event
      detail
      createdAt
    }
    clusterAgentTokens {
      clusterName
      isActive
      description
      lastUsedAt
    }
    agentFleetVersions {
      clusterName
      desiredVersion
      total
      onDesired
      leaderAgentId
      commandsEnabled
      isProduction
    }
    agentReleases {
      version
      image
      notes
      isDefault
      publishedBy
      publishedAt
    }
  }
`;

const MINT = gql`
  mutation MintClusterAgentToken($clusterName: String!, $description: String) {
    mintClusterAgentToken(clusterName: $clusterName, description: $description) {
      clusterName
      token
    }
  }
`;

const ROTATE = gql`
  mutation RotateClusterAgentToken($clusterName: String!) {
    rotateClusterAgentToken(clusterName: $clusterName) {
      clusterName
      token
    }
  }
`;

const SET_ACTIVE = gql`
  mutation SetClusterAgentTokenActive($clusterName: String!, $active: Boolean!) {
    setClusterAgentTokenActive(clusterName: $clusterName, active: $active)
  }
`;

// The server-side half of the command gate. The agent has its own independent
// AGENT_COMMANDS_ENABLED flag in its ConfigMap; BOTH must be on. Root only —
// `isProduction` decides whether a maintainer may approve a mutating command at all,
// so it is not a cosmetic label.
const SET_CLUSTER_SETTINGS = gql`
  mutation SetClusterAgentSettings(
    $clusterName: String!
    $commandsEnabled: Boolean
    $isProduction: Boolean
  ) {
    setClusterAgentSettings(
      clusterName: $clusterName
      commandsEnabled: $commandsEnabled
      isProduction: $isProduction
    ) {
      clusterName
      commandsEnabled
      isProduction
    }
  }
`;

interface Agent {
  agentId: string;
  clusterName: string;
  nodeName?: string | null;
  publicIp?: string | null;
  status: string;
  failedHealthChecks: number;
  lastSeenAt: string;
  agentVersion?: string | null;
  agentImage?: string | null;
  isLeader?: boolean | null;
  leaderHolderIdentity?: string | null;
  leaderObservedAt?: string | null;
  /** Ops this build advertises. Null/empty = the agent hasn't reported any yet. */
  capabilities?: string[] | null;
}
interface StatusEvent {
  id: string;
  agentId: string;
  event: string;
  detail?: string | null;
  createdAt: string;
}
interface ClusterToken {
  clusterName: string;
  isActive: boolean;
  description?: string | null;
  lastUsedAt?: string | null;
}

// status → status-dot level (reuses .health-dot ok/warn/down/none) + display label
const STATUS_META: Record<string, { dot: string; label: string }> = {
  active: { dot: 'ok', label: 'Active' },
  pending_removal: { dot: 'warn', label: 'Pending Removal' },
  removal_failed: { dot: 'down', label: 'Removal failed' },
  removed: { dot: 'none', label: 'Removed' },
  inactive: { dot: 'none', label: 'Inactive' },
};
const statusMeta = (s: string) => STATUS_META[s] ?? { dot: 'none', label: s };

interface AgentGroup extends FleetSummary {
  clusterName: string;
  rows: Agent[];
}

/**
 * What the command dialog was opened for. A cluster and an op, never an agent:
 * the cluster's leader runs every command, so the node-row buttons open the same
 * cluster-addressed dialog as the cluster header's, only with a different prefill.
 */
interface DialogTarget {
  clusterName: string;
  op: string;
  initialArgs?: Record<string, string>;
}

/** A pending root-only flip of one server-side gate, awaiting confirmation. */
interface SettingsChange {
  clusterName: string;
  field: 'commandsEnabled' | 'isProduction';
  next: boolean;
}

/**
 * An agent only runs ops its build advertises. Until it has reported any we
 * can't tell, so we allow the click — the server (and the agent's own
 * deny-by-default registry) is the enforcement point either way.
 */
const supportsOp = (a: Agent, op: string) => !a.capabilities?.length || a.capabilities.includes(op);

/** Default row order inside a cluster: problems first, then most recently seen.
 *  Kept as the `status` sort so the useful default IS a named, re-selectable
 *  column rather than something you lose the moment you click any header. */
const STATUS_RANK = (s: string) => (s === 'active' ? 0 : s === 'pending_removal' ? 1 : 2);

const SORT_PICK: Record<string, (a: Agent) => unknown> = {
  node: (a) => a.nodeName || a.agentId,
  publicIp: (a) => a.publicIp,
  agentVersion: (a) => a.agentVersion,
  isLeader: (a) => !!a.isLeader,
  lastSeenAt: (a) => a.lastSeenAt,
};

function sortAgents(rows: Agent[], sort: ReturnType<typeof useUrlSort>): Agent[] {
  if (sort.key === 'status') {
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort(
      (a, b) =>
        (STATUS_RANK(a.status) - STATUS_RANK(b.status)) * sign ||
        new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime(),
    );
  }
  return compareBy(rows, (a) => SORT_PICK[sort.key]?.(a), sort.dir);
}

export function AgentsPage() {
  const { data, loading, error, refetch } = useQuery<{
    agents: Agent[];
    agentStatusEvents: StatusEvent[];
    clusterAgentTokens: ClusterToken[];
    agentFleetVersions: AgentVersionRollup[];
    agentReleases: AgentRelease[];
  }>(AGENTS, { fetchPolicy: 'cache-and-network' });
  // The checklist's failed-registration line deep-links here, so the page has to be
  // able to answer "which agents". It is a separate document from `Agents` because
  // the failure lives on the LINK row, not on the agent — an agent whose whitelist
  // push failed still reads `active`.
  const checklist = useQuery<{ agentChecklist: AgentChecklist }>(AGENT_CHECKLIST, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const regFailed = useMemo(
    () =>
      new Set(
        (checklist.data?.agentChecklist.failedRegistrations.rows ?? []).map((r) => r.agentId),
      ),
    [checklist.data],
  );
  /**
   * Atlas drift, per agent: this node is heartbeating and its address is NOT on the access
   * list, so its pods cannot reach the database. Like `regFailed` it comes from the
   * checklist rather than from `agents`, because the condition lives on an Atlas row — the
   * agent itself reads `active` (or, after a wrong cascade, `removed`).
   *
   * Kept as agentId → the (project, address) pairs, because the restore names ADDRESSES,
   * not agents: one node's drift can span two projects.
   */
  const driftByAgent = useMemo(() => {
    const m = new Map<string, AtlasRestoreTarget[]>();
    for (const r of checklist.data?.agentChecklist.atlasDrift.rows ?? []) {
      const arr = m.get(r.agentId) ?? [];
      arr.push({ atlasProjectKey: r.atlasProjectKey, ipCidr: r.ipCidr });
      m.set(r.agentId, arr);
    }
    return m;
  }, [checklist.data]);
  /** Every drifted address in the fleet, de-duplicated — what the page-level Restore takes. */
  const allDriftTargets = useMemo(() => {
    const seen = new Set<string>();
    const out: AtlasRestoreTarget[] = [];
    for (const t of [...driftByAgent.values()].flat()) {
      const k = `${t.atlasProjectKey}|${t.ipCidr}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
    return out;
  }, [driftByAgent]);
  const { me } = useMe();
  const role: AppRole = me?.role ?? 'new_member';
  // Preview + draft are root|maintainer on the server — don't offer the buttons
  // to anyone who would only get a 403.
  const canIssue = role === 'root' || role === 'maintainer';
  // setClusterAgentSettings is @RequireRole('root') server-side.
  const canSetSettings = role === 'root';

  const [dialog, setDialog] = useState<DialogTarget | null>(null);
  // Root, mirroring `@RequireRole('root')` on `removeAgents`. Its own predicate rather
  // than a borrowed `canPruneAtlas`: the two are the same rule today for the same
  // reason (one third-party delete, reached from two directions), and naming them
  // separately means the day they diverge it is a code change with a reason attached.
  // The API is the enforcement — this only stops us rendering a button that could
  // return nothing but 403.
  const canRemove = canRemoveAgents(role);
  /** The agents the remove dialog is open for — one row, one selection, or one
   *  cluster's silent agents. The dialog has taken a SET since it was written; what was
   *  missing was a way to name one. */
  const [removing, setRemoving] = useState<{ agentIds: string[]; label: string } | null>(null);
  /** The addresses the restore dialog is open for — one agent's, or the whole drift set. */
  const [restoring, setRestoring] = useState<{ targets: AtlasRestoreTarget[]; label: string } | null>(
    null,
  );
  /** Bulk selection, by agent id. Deliberately NOT in the URL: a filter is a view a
   *  link can carry, a pending set of production deletes is not. It is dropped as
   *  soon as the removal dialog closes, so a stale tick can never ride into a second
   *  run. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [settingsConfirm, setSettingsConfirm] = useState<SettingsChange | null>(null);
  const [setClusterSettings, setClusterSettingsState] = useMutation(SET_CLUSTER_SETTINGS, {
    onCompleted: () => {
      setSettingsConfirm(null);
      refetch();
    },
  });

  // Cluster / status / text filters and the row sort live in the URL, so
  // "/agents?cluster=k8s-optisigns-prod-api" is a link — the command detail
  // page's rail sends you here that way.
  const [clusterFilter, setClusterFilter] = useUrlState('cluster');
  const [statusFilter, setStatusFilter] = useUrlState('status');
  const [q, setQ] = useUrlState('q');
  const sort = useUrlSort('status');

  // An old bookmark or a link in a ticket may still carry `?status=__gone__`. Rewrite
  // it to the red filter rather than only MATCHING it: the dropdown has no `__gone__`
  // option, so a URL that merely worked left the control reading "Any status" over a
  // filtered table — a lying control, and an operator who clears it can never get the
  // view back. One redirect, then the address and the control agree.
  useEffect(() => {
    if (statusFilter === LEGACY_GONE_FILTER) setStatusFilter(RED_FILTER);
  }, [statusFilter, setStatusFilter]);

  const agents = data?.agents ?? [];
  const events = data?.agentStatusEvents ?? [];
  const tokens = data?.clusterAgentTokens ?? [];
  const releases = data?.agentReleases ?? [];
  const rollups = useMemo(
    () => new Map((data?.agentFleetVersions ?? []).map((r) => [r.clusterName, r])),
    [data?.agentFleetVersions],
  );
  const clusterNames = useMemo(
    () => [...new Set(agents.map((a) => a.clusterName))].sort(),
    [agents],
  );
  // The fleet's freshness is the newest heartbeat in it — one silent agent is a
  // per-row problem, but the whole fleet going quiet means the register endpoint
  // or the token is broken.
  const newestSeen = useMemo(
    () => agents.map((a) => a.lastSeenAt).filter(Boolean).sort().at(-1),
    [agents],
  );

  const fleetRemoved = useMemo(() => agents.filter((a) => isRemovedAgent(a)).length, [agents]);
  const needle = q.trim().toLowerCase();
  const matching = useMemo(
    () =>
      agents.filter(
        (a) =>
          (!clusterFilter || a.clusterName === clusterFilter) &&
          // `red` is a heartbeat verdict, not a value of the `status` column — the
          // whole point is that status still says `active`. It has to be matched
          // separately or picking it would filter everything away.
          // It excludes `removed`: silence is how a removed agent looks forever, so
          // without that an operator who has just reclaimed 14 agents still finds all
          // 14 sitting under the red filter with a live Remove button. `removed`
          // remains reachable — as its own status option.
          // `__gone__` is the retired 4 h sentinel, aliased so an old bookmark or a
          // link in a ticket lands on the red list rather than on an empty table.
          (!statusFilter ||
            (statusFilter === RED_FILTER || statusFilter === LEGACY_GONE_FILTER
              ? isRedAgentRow(a)
              : statusFilter === REG_FAILED_FILTER
                ? regFailed.has(a.agentId)
                : statusFilter === DRIFT_FILTER
                  ? driftByAgent.has(a.agentId)
                  : a.status === statusFilter)) &&
          (!needle ||
            (a.nodeName ?? '').toLowerCase().includes(needle) ||
            a.agentId.toLowerCase().includes(needle) ||
            (a.publicIp ?? '').toLowerCase().includes(needle)),
      ),
    [agents, clusterFilter, statusFilter, needle, regFailed, driftByAgent],
  );

  const matchingRemoved = useMemo(() => matching.filter((a) => isRemovedAgent(a)).length, [matching]);

  // Group the fleet by cluster (legacy layout): each group carries active/pending/other counts.
  const groups = useMemo<AgentGroup[]>(() => {
    const byCluster = new Map<string, Agent[]>();
    for (const a of matching) {
      const arr = byCluster.get(a.clusterName) ?? [];
      arr.push(a);
      byCluster.set(a.clusterName, arr);
    }
    return [...byCluster.entries()]
      .map(([clusterName, list]) => ({
        clusterName,
        rows: sortAgents(list, sort),
        // Every count comes from ONE place (`summarizeAgents`), which knows the
        // clock and the status column: red is derived from the HEARTBEAT (the
        // `status` column reads `active` for a month-silent agent) and `removed` is
        // subtracted from all of it, because that agent has already been dealt with.
        ...summarizeAgents(list),
      }))
      .sort((a, b) => a.clusterName.localeCompare(b.clusterName));
  }, [matching, sort]);

  // Groups start collapsed; a cluster is open only once expanded (so new clusters
  // default collapsed too). The map records an EXPLICIT choice per cluster, open or
  // shut — `Set` could only ever say "opened", so while a filter forced the default
  // open the toggle had nothing to express and the header read as a dead control
  // (reproduced by QC on `?status=<anything>`). A click now always wins over the
  // default; clearing the filter does not resurrect a stale choice, because the
  // default is only consulted for clusters the operator has not touched.
  const [openState, setOpenState] = useState<Map<string, boolean>>(new Map());
  const toggleGroup = (name: string, open: boolean) =>
    setOpenState((prev) => new Map(prev).set(name, open));
  // Filtering to one cluster is an act of asking to SEE it — arriving at a
  // collapsed group would make the deep link look like it had matched nothing.
  // Same reason a single group opens: arriving from the Overview checklist on
  // `?status=__red__` and finding every cluster collapsed makes the deep link look
  // like it matched nothing — the count is in the header, but the ROW the line is
  // about is what the operator came for.
  const isOpen = (name: string) => openState.get(name) ?? (groups.length === 1 || !!statusFilter);
  // Only rows still in view can stay ticked: a removed agent leaves the red filter the
  // moment the refetch lands, and a tick that outlives its row would be named to a
  // second confirm nobody can see.
  const selectedRows = useMemo(
    () => matching.filter((a) => selected.has(a.agentId) && isBulkSelectable(a)),
    [matching, selected],
  );
  const toggleSelected = (agentId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  const clearSelection = () => setSelected(new Set());
  /** How many of a cluster's selectable agents are ticked — drives the header
   *  checkbox's checked / indeterminate state. */
  const groupPicked = (g: AgentGroup) =>
    g.rows.filter((a) => isBulkSelectable(a) && selected.has(a.agentId)).length;
  const pickGroup = (g: AgentGroup, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const a of g.rows.filter((a) => isBulkSelectable(a))) {
        if (on) next.add(a.agentId);
        else next.delete(a.agentId);
      }
      return next;
    });

  const anyFilter = Boolean(clusterFilter || statusFilter || needle);
  const clearFilters = () => {
    setClusterFilter('');
    setStatusFilter('');
    setQ('');
  };

  return (
    <div className="page">
      <div className="detail-head">
        <h2>Agents</h2>
        <span className="badge quiet">
          {agents.length} agent{agents.length === 1 ? '' : 's'}
        </span>
        <span className="spacer" />
        <Link className="btn btn-ghost" to="/cluster-access/commands">
          Command log
        </Link>
      </div>
      <p className="detail-desc">
        DaemonSet agents report each node&rsquo;s egress IP for MongoDB Atlas IP
        whitelisting. Agents
        authenticate with a per-cluster token; reconcile pushes reported IPs to Atlas.
        Operations (logs, restarts, upgrades) go through the{' '}
        <Link to="/docs/agents">preview → draft → approve</Link> command plane — nothing runs
        that a human has not read as a diff.
      </p>

      {error && (
        <>
          <p className="error">Couldn&apos;t load the fleet — the API didn&apos;t respond.</p>
          <p className="muted">
            Agents keep heartbeating regardless; this page only reads. If it persists, ask in
            #devops-infra.
          </p>
        </>
      )}

      <div className="detail-sections">
        <section>
          <h3>Fleet</h3>
          {/* An hour, not a day: agents heartbeat every few minutes, so a fleet
              whose newest beat is an hour old is already a finding. */}
          <ScannedAt at={newestSeen} staleAfterH={1} lead="Newest heartbeat in the fleet" />

          <div className="filter-row">
            {/* Was a bespoke ghost text button beside the page heading — the one page
                that already had a refresh, and the reason F5 spread everywhere else.
                Same icon control, same toolbar position as every other table now. */}
            <RefreshButton refetch={() => refetch().catch(() => undefined)} />
            <input
              type="search"
              placeholder="Search node, agent id, or IP…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              value={clusterFilter}
              onChange={(e) => setClusterFilter(e.target.value)}
              title="filter by cluster"
            >
              <option value="">All clusters</option>
              {clusterNames.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              title="filter by status"
            >
              <option value="">Any status</option>
              {/* The checklist's deep link lands here. `__red__` is a heartbeat
                  verdict, not a value of the `status` column — and it is the ONLY one
                  of the two warning states that reaches the Overview: the warning state
                  stays on this page by design. */}
              <option value={RED_FILTER}>Silent (&gt; {AGENT_RED_MINUTES} min)</option>
              <option value={REG_FAILED_FILTER}>Whitelisting failed</option>
              <option value={DRIFT_FILTER}>Missing from Atlas</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            {/* Removed agents are counted APART, never folded into the fleet total:
                "370 of 370" while 14 of them have already been reclaimed reads as
                work outstanding. They stay visible — the rows are the audit trail —
                but they are not part of the number an operator is working down. */}
            <span className="filter-count">
              {matching.length > matchingRemoved || matchingRemoved === 0 ? (
                <>
                  {matching.length - matchingRemoved} of {agents.length - fleetRemoved}
                  {matchingRemoved > 0 && (
                    <span className="muted"> · {matchingRemoved} removed</span>
                  )}
                </>
              ) : (
                // Every row in view is a reclaimed one — the `Removed` status option, or a
                // search that only matched those. The live pair would read "0 of 356" over
                // the rows the operator opened this filter to audit, so the removed count
                // leads instead. Still counted apart; just not counted as nothing.
                <>
                  {matchingRemoved} of {fleetRemoved} <span className="muted">removed</span>
                </>
              )}
            </span>
          </div>

          {/* The checklist's "Review and restore" link lands here on ?status=__drift__.
              One action for the whole set, above the rows it is about — the same place and
              the same shape as the bulk-removal bar, because it is the same kind of
              decision in the opposite direction. Root only, like the mutation. */}
          {canRemove && statusFilter === DRIFT_FILTER && allDriftTargets.length > 0 && (
            <div className="agent-select-bar" role="status" aria-live="polite">
              <strong>
                {allDriftTargets.length} address{allDriftTargets.length === 1 ? '' : 'es'} missing
                from Atlas
              </strong>
              <span className="muted">
                these nodes are heartbeating but cannot reach the database — nothing puts the
                addresses back on its own
              </span>
              <span className="spacer" />
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() =>
                  setRestoring({
                    targets: allDriftTargets,
                    label: `${allDriftTargets.length} missing address${allDriftTargets.length === 1 ? '' : 'es'}`,
                  })
                }
              >
                Restore all&hellip;
              </button>
            </div>
          )}

          {canRemove && selectedRows.length > 0 && (
            // The selection bar is the only place a multi-agent removal can be
            // started from the rows themselves. It sits above the list rather than
            // floating over it: the list is what the operator is reading, and a bar
            // that covers the bottom rows hides the ones they just ticked.
            <div className="agent-select-bar" role="status" aria-live="polite">
              <strong>
                {selectedRows.length} agent{selectedRows.length === 1 ? '' : 's'} selected
              </strong>
              {/* One clock, so one sentence. It says what is true of every selectable
                  row and stops there — calling the set reclaimable is the claim this
                  bar must not make, because the preview is where each address is
                  judged. */}
              <span className="muted">
                silent more than {AGENT_RED_MINUTES} min — the preview says what each one holds
              </span>
              <span className="spacer" />
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() =>
                  setRemoving({
                    agentIds: selectedRows.map((a) => a.agentId),
                    label: `${selectedRows.length} selected agent${selectedRows.length === 1 ? '' : 's'}`,
                  })
                }
              >
                Remove selected&hellip;
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={clearSelection}>
                Clear
              </button>
            </div>
          )}

          {loading && !agents.length ? (
            <div className="skeleton-row-group">
              {[0, 1, 2, 3].map((i) => (
                <div className="skeleton-row" key={i} />
              ))}
            </div>
          ) : !agents.length ? (
            <EmptyState
              icon="cluster"
              title="No agents yet"
              body="An agent appears here the first time its DaemonSet registers with a per-cluster token. Mint one below, then roll the DaemonSet onto the cluster."
              actionLabel="How agents work"
              actionTo="/docs/agents"
            />
          ) : !groups.length ? (
            <p className="muted">
              No agent matches these filters.{' '}
              {anyFilter && (
                <button className="mini-btn" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </p>
          ) : (
            <div className="agent-groups">
              {groups.map((g) => {
                const open = isOpen(g.clusterName);
                const roll = rollups.get(g.clusterName);
                const desired = roll?.desiredVersion ?? null;
                const onDesired = roll ? roll.onDesired === roll.total : false;
                const commandsEnabled = roll?.commandsEnabled ?? false;
                const bodyId = `agents-${g.clusterName.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                // Point the upgrade at the release the cluster is supposed to be on
                // (else the default release) so the operator isn't typing image refs.
                const rel =
                  releases.find((r) => r.version === desired) ?? releases.find((r) => r.isDefault) ?? null;
                return (
                  <div
                    className={`agent-group${g.red > 0 ? ' agent-group--alert' : ''}`}
                    key={g.clusterName}
                  >
                    {/* Header is a ROW, not a button — action controls live inside it
                        and a <button> inside a <button> is invalid (Firefox/Safari
                        swallow the inner click). Only the label toggles. */}
                    <div className="agent-group-head">
                      <button
                        type="button"
                        className="agent-group-toggle"
                        aria-expanded={open}
                        aria-controls={bodyId}
                        onClick={() => toggleGroup(g.clusterName, !open)}
                      >
                        <span className={`agent-caret ${open ? 'open' : ''}`}>▸</span>
                        <strong>{g.clusterName}</strong>
                        {/* "0 agents" over 14 visible rows reads as "nothing matched"
                            — the same trap the filter count had. When every row in view
                            is a reclaimed one, the removed chip is the only count. */}
                        {(g.live > 0 || g.removed === 0) && (
                          <span className="badge quiet">
                            {g.live} agent{g.live === 1 ? '' : 's'}
                          </span>
                        )}
                        {/* Reclaimed agents are shown, not hidden — an operator
                            auditing what a cascade did has to be able to find them —
                            but they are their own quiet count, outside the fleet the
                            other chips describe. */}
                        {g.removed > 0 && (
                          <span
                            className="badge quiet"
                            title="Agents an operator has already removed: their Atlas addresses were reclaimed and nothing here needs doing"
                          >
                            {g.removed} removed
                          </span>
                        )}
                        {desired && <span className="badge">desired {desired}</span>}
                        {roll && (
                          <span className={`agent-count ${onDesired ? 'ok' : 'warn'}`}>
                            {roll.onDesired}/{roll.total} on desired
                          </span>
                        )}
                        <span className="spacer" />
                        {g.red > 0 && (
                          <span
                            className="agent-count silent"
                            title={`${g.red} agent${g.red === 1 ? '' : 's'} have sent no heartbeat for more than ${AGENT_RED_MINUTES} min — open Remove to see what each one holds on Atlas`}
                          >
                            {g.red} silent
                          </span>
                        )}
                        {g.active > 0 && <span className="agent-count ok">{g.active} active</span>}
                        {g.pending > 0 && <span className="agent-count warn">{g.pending} pending</span>}
                        {g.other > 0 && <span className="agent-count muted">{g.other} other</span>}
                      </button>
                      <div className="agent-group-actions">
                        {/* The DOKS-upgrade shape, in one control: every hop leaves a
                            cluster's worth of dead agents holding Atlas slots, and
                            "remove all the silent agents of this cluster" is the whole
                            job. It opens the same preview→confirm dialog as one row —
                            nothing is deleted until the operator reads what it owns.
                            One clock, so one wording: "silent" everywhere, from the row
                            label to this button to the dialog heading. */}
                        {canRemove && g.red > 0 && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm agent-group-remove"
                            title={`Preview the Atlas footprint of the ${g.red} agent${g.red === 1 ? '' : 's'} in ${g.clusterName} that have sent no heartbeat for more than ${AGENT_RED_MINUTES} min`}
                            onClick={() =>
                              setRemoving({
                                agentIds: g.rows.filter((a) => isBulkSelectable(a)).map((a) => a.agentId),
                                label: `${g.red} silent agent${g.red === 1 ? '' : 's'} in ${g.clusterName}`,
                              })
                            }
                          >
                            Remove {g.red} silent agent{g.red === 1 ? '' : 's'}&hellip;
                          </button>
                        )}
                        {/* Root can flip both server-side gates here; everyone else sees
                            the same information as a read-only badge. Enabling commands is
                            only HALF the gate — the cluster's agents also need
                            AGENT_COMMANDS_ENABLED in their ConfigMap. */}
                        {roll?.isProduction &&
                          (canSetSettings ? (
                            <button
                              type="button"
                              className="badge prod as-button"
                              title="Clear the production flag — lets a maintainer approve mutating commands here"
                              onClick={() =>
                                setSettingsConfirm({
                                  clusterName: g.clusterName,
                                  field: 'isProduction',
                                  next: false,
                                })
                              }
                            >
                              production
                            </button>
                          ) : (
                            <span className="badge prod">production</span>
                          ))}
                        {/* Same pill for everyone — a maintainer deciding whether to
                            approve needs to see the gate too; only root can click it. */}
                        {canSetSettings ? (
                          <button
                            type="button"
                            className={`badge gate as-button ${commandsEnabled ? 'gate-on' : 'gate-off'}`}
                            title={
                              commandsEnabled
                                ? 'Commands are enabled server-side — click to disable'
                                : 'Commands are disabled server-side — click to enable'
                            }
                            onClick={() =>
                              setSettingsConfirm({
                                clusterName: g.clusterName,
                                field: 'commandsEnabled',
                                next: !commandsEnabled,
                              })
                            }
                          >
                            <span className="gate-dot" aria-hidden="true" />
                            commands {commandsEnabled ? 'on' : 'off'}
                          </button>
                        ) : (
                          <span
                            className={`badge gate ${commandsEnabled ? 'gate-on' : 'gate-off'}`}
                            title={
                              commandsEnabled
                                ? 'Commands are enabled for this cluster'
                                : 'Commands are disabled for this cluster'
                            }
                          >
                            <span className="gate-dot" aria-hidden="true" />
                            commands {commandsEnabled ? 'on' : 'off'}
                          </span>
                        )}
                        {/* Actions collapse into a `⋯`, the gate badges above do not:
                            those are STATE an operator reads while scanning the list,
                            and hiding them behind a click would make "is this cluster
                            armed?" a per-cluster interaction. */}
                        <KebabMenu label={`Actions for ${g.clusterName}`}>
                          {/* The read-only rung of the rollout ladder needs a way in. With
                              commands on but AGENT_MUTATING_OPS_ENABLED / pod.logs still off,
                              an agent advertises only pod.list + workload.status — and every
                              other control here issues an op it is not advertising, so
                              the safe intermediate state had nothing you could actually run. */}
                          {canIssue && (
                            <button
                              type="button"
                              disabled={!commandsEnabled}
                              title={
                                commandsEnabled
                                  ? 'List pods in the agent namespace — read-only, the safest way to prove the command path'
                                  : 'Commands are disabled for this cluster'
                              }
                              onClick={() =>
                                setDialog({
                                  clusterName: g.clusterName,
                                  op: 'pod.list',
                                  initialArgs: { namespace: 'platform' },
                                })
                              }
                            >
                              Pods
                            </button>
                          )}
                          {canIssue && (
                            <button
                              type="button"
                              disabled={!commandsEnabled || onDesired}
                              title={
                                !commandsEnabled
                                  ? 'Commands are disabled for this cluster'
                                  : onDesired
                                    ? 'Every agent is already on the desired version'
                                    : `Roll the DaemonSet to ${rel?.version ?? 'a published release'}`
                              }
                              onClick={() =>
                                setDialog({
                                  clusterName: g.clusterName,
                                  op: 'agent.self_upgrade',
                                  initialArgs: rel ? { image: rel.image } : {},
                                })
                              }
                            >
                              Upgrade
                            </button>
                          )}
                          <Link to={`/cluster-access/commands?cluster=${encodeURIComponent(g.clusterName)}`}>
                            Command log
                          </Link>
                        </KebabMenu>
                      </div>
                    </div>
                    {open && (
                      <table className="mtx agent-table" id={bodyId}>
                        <thead>
                          {/* One `sort` shared by every open group — a column means
                              the same thing in each, and per-group sort state would
                              make two clusters disagree about what "Version ↑" is. */}
                          <tr>
                            {canRemove && (
                              <th className="agent-pick-col">
                                {/* Select every silent agent in THIS cluster. The
                                    header action beside the cluster name does the
                                    same job in one click; this is the way in for an
                                    operator who wants to untick a couple first. */}
                                <input
                                  type="checkbox"
                                  aria-label={`Select the silent agents in ${g.clusterName}`}
                                  title={
                                    g.red > 0
                                      ? `Select the ${g.red} silent agent${g.red === 1 ? '' : 's'} in this cluster`
                                      : 'No silent agents in this cluster'
                                  }
                                  disabled={g.red === 0}
                                  checked={g.red > 0 && groupPicked(g) === g.red}
                                  ref={(el) => {
                                    if (el)
                                      el.indeterminate = groupPicked(g) > 0 && groupPicked(g) < g.red;
                                  }}
                                  onChange={(e) => pickGroup(g, e.target.checked)}
                                />
                              </th>
                            )}
                            <SortHeader
                              label="Status"
                              sortKey="status"
                              sort={sort}
                              title="Sort by status — problems first, then most recently seen"
                            />
                            <SortHeader label="Node name" sortKey="node" sort={sort} />
                            <SortHeader label="IP address" sortKey="publicIp" sort={sort} />
                            <SortHeader label="Version" sortKey="agentVersion" sort={sort} />
                            <SortHeader label="Leader" sortKey="isLeader" sort={sort} />
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((a) => {
                            const meta = statusMeta(a.status);
                            const outdated = !!desired && !!a.agentVersion && a.agentVersion !== desired;
                            // `removed` outranks the clock: the agent is silent
                            // forever by construction and an operator has already
                            // dealt with it, so it carries no tint, no warning word
                            // and no action.
                            const removed = isRemovedAgent(a);
                            // Green · yellow · red, from the one clock this app has.
                            const level = agentLevel(a);
                            return (
                              <tr key={a.agentId} className={agentRowClass(a)}>
                                {canRemove && (
                                  <td className="agent-pick-col">
                                    {isBulkSelectable(a) ? (
                                      <input
                                        type="checkbox"
                                        checked={selected.has(a.agentId)}
                                        onChange={() => toggleSelected(a.agentId)}
                                        aria-label={`Select ${a.nodeName || a.agentId} for removal`}
                                      />
                                    ) : (
                                      // Nothing to tick, and the reason is worth
                                      // saying: a node that is still reporting is not
                                      // a reclaim candidate, and a removed one holds
                                      // no address at all.
                                      <span
                                        className="muted"
                                        title={
                                          removed
                                            ? 'Already removed — its Atlas addresses were reclaimed'
                                            : `Only agents silent for more than ${AGENT_RED_MINUTES} min can be selected — this node is still reporting`
                                        }
                                        aria-hidden="true"
                                      >
                                        ·
                                      </span>
                                    )}
                                  </td>
                                )}
                                <td>
                                  {/* The tooltip reads the HEARTBEAT, like the label beside
                                      it. It used to carry `meta.label`, i.e. `status` —
                                      which reads Active fleet-wide — so hovering a silent
                                      row said "Active". It now states both states of the
                                      one clock, and nothing else. */}
                                  <span
                                    className="dot-cell"
                                    title={
                                      removed
                                        ? `Removed by an operator — its Atlas addresses were reclaimed. Last heartbeat ${timeAgo(a.lastSeenAt)}`
                                        : a.lastSeenAt
                                          ? `Last heartbeat ${timeAgo(a.lastSeenAt)} · quiet = ${YELLOW_RULE} · silent = ${RED_RULE}`
                                          : `No heartbeat recorded · quiet = ${YELLOW_RULE} · silent = ${RED_RULE}`
                                    }
                                  >
                                    <span
                                      className={`health-dot ${level === 'red' ? 'down' : level === 'yellow' ? 'warn' : meta.dot}`}
                                    />
                                    {/* `status` still reads Active for a node that has been
                                        silent for weeks — the watchdog that would say
                                        otherwise is switched off. Say what the heartbeat
                                        says, not what the column says. */}
                                    <span
                                      className={
                                        level !== 'green'
                                          ? `stale-label stale-label--${level}`
                                          : meta.dot === 'none'
                                            ? 'muted'
                                            : ''
                                      }
                                    >
                                      {level === 'red' ? 'Silent' : level === 'yellow' ? 'Quiet' : meta.label}
                                    </span>
                                  </span>
                                </td>
                                <td>
                                  {/* Node names, IPs and version strings are resource
                                      identifiers, not code — Plex Sans per the mono
                                      policy ratified in phase 6 §1. */}
                                  <div className="agent-node">{a.nodeName || a.agentId}</div>
                                  <div
                                    className={`agent-sub ${level === 'red' && !removed ? 'agent-sub-silent' : 'muted'}`}
                                  >
                                    Last seen: {timeAgo(a.lastSeenAt)}
                                  </div>
                                  {removed ? (
                                    // Says what was done, so the row reads as a
                                    // record rather than as an outstanding problem.
                                    <div className="agent-sub muted">
                                      Removed — its Atlas addresses were reclaimed
                                    </div>
                                  ) : level !== 'green' ? (
                                    // Says what it does NOT mean, because the colour
                                    // invites the opposite reading: both states are the
                                    // human early-warning path and change nothing about
                                    // Atlas. A removal is still a preview and a confirm.
                                    <div className={`agent-sub stale-label--${level}`}>
                                      {level === 'red' ? RED_RULE : YELLOW_RULE} — nothing on Atlas
                                      has changed
                                    </div>
                                  ) : null}
                                  {regFailed.has(a.agentId) && (
                                    <div className="agent-sub agent-sub-silent">
                                      Whitelisting failed — its address never reached an Atlas
                                      access list
                                    </div>
                                  )}
                                  {driftByAgent.has(a.agentId) && (
                                    // The one case where a row that looks fine — or worse,
                                    // reads `Removed` — is an active outage for the pods on
                                    // that node. Say it on the row, not only on the Overview.
                                    //
                                    // Worded from the ADDRESS, not from the node's liveness:
                                    // the drift detector only reports heartbeating agents, but
                                    // this row renders its own status from a heartbeat read
                                    // milliseconds later, so "this node is reporting" could
                                    // land directly under a `Silent` label and make the two
                                    // halves of one row contradict each other. What is
                                    // certainly true is that the address is off the list.
                                    <div className="agent-sub agent-sub-silent">
                                      Missing from Atlas — its address is not on the access list,
                                      so its pods cannot reach the database
                                    </div>
                                  )}
                                  {a.status === 'pending_removal' && (
                                    <div className="agent-sub agent-sub-warn">
                                      Auto: {a.failedHealthChecks} health check
                                      {a.failedHealthChecks === 1 ? '' : 's'} failed
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <span className="ip-cell">{a.publicIp || '—'}</span>
                                  {a.publicIp && <CopyButton value={a.publicIp} />}
                                </td>
                                <td>
                                  {a.agentVersion || '—'}
                                  {outdated && <span className="badge inferred">outdated</span>}
                                  {a.agentImage && (
                                    <div className="agent-sub muted" title={a.agentImage}>
                                      {a.agentImage.split('/').pop()}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {a.isLeader ? (
                                    <>
                                      <span className="badge confirmed">leader</span>
                                      <div className="agent-sub muted">
                                        seen {timeAgo(a.leaderObservedAt)}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="muted">—</span>
                                  )}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {canIssue ? (
                                    <>
                                      {/* Neither button passes this row's agent id. Only the
                                          cluster's leader asks for work, and neither op is
                                          node-local, so a command addressed to a non-leader row
                                          was never picked up. Restart patches the DaemonSet, so
                                          it restarts every node's pod, not this row's. */}
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        disabled={!commandsEnabled || !supportsOp(a, 'pod.logs')}
                                        title={opTitle(
                                          commandsEnabled,
                                          supportsOp(a, 'pod.logs'),
                                          'pod.logs',
                                          "Read a pod's log through the cluster leader (pod.logs)",
                                        )}
                                        onClick={() =>
                                          setDialog({
                                            clusterName: g.clusterName,
                                            op: 'pod.logs',
                                            initialArgs: { namespace: 'platform' },
                                          })
                                        }
                                      >
                                        Logs
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        disabled={!commandsEnabled || !supportsOp(a, 'daemonset.restart')}
                                        title={opTitle(
                                          commandsEnabled,
                                          supportsOp(a, 'daemonset.restart'),
                                          'daemonset.restart',
                                          'Restart the agent DaemonSet on every node of this cluster (daemonset.restart)',
                                        )}
                                        onClick={() =>
                                          setDialog({
                                            clusterName: g.clusterName,
                                            op: 'daemonset.restart',
                                            initialArgs: { name: 'optisigns-agent', namespace: 'platform' },
                                          })
                                        }
                                      >
                                        Restart
                                      </button>
                                    </>
                                  ) : (
                                    <span className="muted text-2xs">root / maintainer only</span>
                                  )}
                                  {/* Root only — the same gate as the Atlas prune, and
                                      for the same reason: it deletes from a live
                                      production access list. Absent, not disabled, for
                                      everyone else. */}
                                  {canRemove && isRemovableAgent(a) && (
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm"
                                      title="See this agent's Atlas footprint, then reclaim it — nothing is deleted until you confirm"
                                      onClick={() =>
                                        setRemoving({
                                          agentIds: [a.agentId],
                                          label: a.nodeName || a.agentId,
                                        })
                                      }
                                    >
                                      Remove&hellip;
                                    </button>
                                  )}
                                  {/* Offered only on a row that IS drifted, so it never
                                      reads as a general undo. It is shown even when Remove
                                      is not (a `removed` agent still beating is exactly the
                                      case restore exists for). Nothing is written until the
                                      preview is confirmed. */}
                                  {canRemove && driftByAgent.has(a.agentId) && (
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm"
                                      title="This node is heartbeating but its address is missing from Atlas — preview putting it back"
                                      onClick={() =>
                                        setRestoring({
                                          targets: driftByAgent.get(a.agentId) ?? [],
                                          label: a.nodeName || a.agentId,
                                        })
                                      }
                                    >
                                      Restore&hellip;
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <ClusterTokensSection
          tokens={tokens}
          onChange={() => refetch()}
          refetch={() => refetch().catch(() => undefined)}
        />

        <section style={{ marginTop: '1.5rem' }}>
          <div className="section-head">
            <h3>Recent events</h3>
            <span className="spacer" />
            <RefreshButton refetch={() => refetch().catch(() => undefined)} />
          </div>
          {!events.length ? (
            <p className="muted">No agent events yet.</p>
          ) : (
            <table className="mtx">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Agent</th>
                  <th>Event</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="muted">{new Date(e.createdAt).toLocaleString()}</td>
                    <td>{e.agentId}</td>
                    <td>{e.event}</td>
                    <td className="muted">{e.detail || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {removing && (
        <AgentRemoveDialog
          agentIds={removing.agentIds}
          label={removing.label}
          onClose={() => {
            setRemoving(null);
            // Ticks do not survive the dialog: whatever happened, the rows underneath
            // have just changed meaning.
            clearSelection();
            // NO refetch here. The confirm already refetches `Agents` and waits for it
            // (`awaitRefetchQueries`), so this second round-trip only re-sorted the
            // list a second time under the operator's cursor — which is how a click
            // got swallowed mid-run during the beta removal. Closing the dialog is now
            // one relayout, not two.
          }}
        />
      )}
      {restoring && (
        <AtlasRestoreDialog
          targets={restoring.targets}
          label={restoring.label}
          onClose={() => setRestoring(null)}
          onRestored={() => clearSelection()}
        />
      )}

      {dialog && (
        <AgentCommandDialog
          clusterName={dialog.clusterName}
          op={dialog.op}
          initialArgs={dialog.initialArgs}
          role={role}
          onClose={() => {
            setDialog(null);
            refetch();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(settingsConfirm)}
        title={settingsConfirm ? settingsConfirmTitle(settingsConfirm) : ''}
        body={settingsConfirm ? settingsConfirmBody(settingsConfirm) : undefined}
        confirmLabel={settingsConfirmLabel(settingsConfirm)}
        // Loosening a gate is the risky direction, so that is what renders red:
        // turning commands ON, or clearing the production flag.
        danger={
          settingsConfirm
            ? settingsConfirm.field === 'isProduction'
              ? !settingsConfirm.next
              : settingsConfirm.next
            : false
        }
        busy={setClusterSettingsState.loading}
        onCancel={() => setSettingsConfirm(null)}
        onConfirm={() => {
          if (!settingsConfirm) return;
          void setClusterSettings({
            variables: {
              clusterName: settingsConfirm.clusterName,
              [settingsConfirm.field]: settingsConfirm.next,
            },
          });
        }}
      />
    </div>
  );
}

/** Name the ACTION, not the boolean — "Disable" for clearing a production flag reads
 *  as "disable the cluster", which is the opposite of what the click does. */
function settingsConfirmLabel(c: SettingsChange | null): string {
  if (!c) return 'Confirm';
  if (c.field === 'isProduction') return c.next ? 'Mark production' : 'Clear flag';
  return c.next ? 'Enable' : 'Disable';
}

function settingsConfirmTitle(c: SettingsChange): string {
  return c.field === 'commandsEnabled'
    ? `${c.next ? 'Enable' : 'Disable'} commands on ${c.clusterName}?`
    : `Clear the production flag on ${c.clusterName}?`;
}

function settingsConfirmBody(c: SettingsChange): string {
  if (c.field === 'isProduction') {
    return `A maintainer will be able to approve MUTATING commands on ${c.clusterName}; today that needs root. Only clear this on a cluster that genuinely is not production.`;
  }
  return c.next
    ? `Approved commands become dispatchable to ${c.clusterName}. The cluster's agents must also have AGENT_COMMANDS_ENABLED=true in their ConfigMap — this switch alone does nothing.`
    : `New commands can no longer be approved for ${c.clusterName}. Anything already queued or running is unaffected.`;
}

/**
 * Why an action button is disabled — never leave a dead control unexplained.
 * When it is live, `enabled` says what the click reaches (defaults to the op).
 */
function opTitle(commandsEnabled: boolean, supported: boolean, op: string, enabled: string = op): string {
  if (!commandsEnabled) return 'Commands are disabled for this cluster';
  if (!supported) return `This agent build does not advertise ${op}`;
  return enabled;
}

function ClusterTokensSection({
  tokens,
  onChange,
  refetch,
}: {
  tokens: ClusterToken[];
  onChange: () => void;
  refetch: () => unknown;
}) {
  const [newCluster, setNewCluster] = useState('');
  const [issued, setIssued] = useState<{ clusterName: string; token: string } | null>(null);

  const [mint, mintState] = useMutation<{ mintClusterAgentToken: { clusterName: string; token: string } }>(MINT, {
    onCompleted: (d) => {
      setIssued(d.mintClusterAgentToken);
      setNewCluster('');
      onChange();
    },
  });
  const [rotate, rotateState] = useMutation<{ rotateClusterAgentToken: { clusterName: string; token: string } }>(
    ROTATE,
    { onCompleted: (d) => { setIssued(d.rotateClusterAgentToken); onChange(); } },
  );
  const [setActive] = useMutation(SET_ACTIVE, { onCompleted: onChange });

  const busy = mintState.loading || rotateState.loading;

  return (
    <section style={{ marginTop: '1.5rem' }}>
      <h3>Agent cluster tokens</h3>
      <p className="muted" style={{ marginBottom: '0.5rem' }}>
        Per-cluster bearer token the DaemonSet sends (<code>ops-cluster-name</code> +{' '}
        <code>Authorization: Bearer</code>). Shown once at mint/rotate.
      </p>

      {issued && (
        <p className="detail-desc" style={{ background: 'var(--surface-2, #f5f5f5)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
          {/* The TOKEN stays in <code> — a secret being copied verbatim is one of
              the uses the mono policy explicitly keeps. The cluster name is not. */}
          Token for {issued.clusterName} — copy now, it won&rsquo;t be shown again:
          <br />
          <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{issued.token}</code>
          <br />
          <button type="button" className="btn btn-ghost" onClick={() => setIssued(null)}>
            Dismiss
          </button>
        </p>
      )}

      <div className="filter-row" style={{ marginBottom: '0.75rem' }}>
        <input
          className="class-select"
          placeholder="cluster name (e.g. k8s-optisigns-qa)"
          value={newCluster}
          onChange={(e) => setNewCluster(e.target.value)}
          aria-label="cluster name"
          style={{ minWidth: 280 }}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={!newCluster.trim() || busy}
          onClick={() => mint({ variables: { clusterName: newCluster.trim() } })}
        >
          {mintState.loading ? 'Minting…' : 'Mint token'}
        </button>
      </div>
      {(mintState.error || rotateState.error) && (
        <>
          <p className="error">Couldn&apos;t issue the token.</p>
          <p className="muted">
            No token was created or rotated, so the cluster&apos;s agents keep working with the one
            they have. Check the cluster name and retry.
          </p>
        </>
      )}

      <div className="filter-row">
        <RefreshButton refetch={refetch} />
      </div>
      {!tokens.length ? (
        <p className="muted">No cluster tokens yet.</p>
      ) : (
        <table className="mtx">
          <thead>
            <tr>
              <th>Cluster</th>
              <th>Active</th>
              <th>Last used</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.clusterName}>
                <td>{t.clusterName}</td>
                <td>
                  <span className={`badge ${t.isActive ? 'ok' : 'quiet'}`}>{t.isActive ? 'active' : 'disabled'}</span>
                </td>
                <td className="muted">{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : 'never'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => rotate({ variables: { clusterName: t.clusterName } })}
                  >
                    Rotate
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setActive({ variables: { clusterName: t.clusterName, active: !t.isActive } })}
                  >
                    {t.isActive ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
